/**
 * A-GTCC-RED1 — which page the admin bundle opens on after a login.
 *
 * # The defect this exists to remove
 *
 * `apps/admin` is built TWICE from the same source (see the edge
 * Dockerfile): once with vite `base=/admin/` and served under `/admin/`
 * on the customer origin, and once with `VITE_BASE=/` and served at the
 * ROOT of the Control Cockpit origin. The BFF already lands a login back
 * on the origin it started from (services/api/src/auth/auth-origins.ts,
 * proven by services/api/src/auth/post-login-origin.test.ts), so an
 * operator signing in at the cockpit arrives at the cockpit URL — and
 * then sees the Admin Overview, because the bundle's initial page was a
 * process-wide constant (`useState<AdminPage>("overview")`).
 *
 * Right URL, wrong surface. The URL half was fixed in the BFF; this is
 * the remaining half, and it lives in the client because "which page"
 * is a client-side state switch (the admin console has no router — see
 * the routing note in AdminApp.tsx).
 *
 * # Why the surface marker and not the hostname, and not `base`
 *
 * The cockpit origin's hostname is deployment configuration
 * (`GTCC_DOMAIN`), so a client-side host check would be a second place
 * that has to agree with the edge — the exact drift class the auth
 * origin registry was built to avoid.
 *
 * `import.meta.env.BASE_URL` was the first answer, and it was wrong:
 * the edge builds TWO root-based artifacts, `/srv/gtcc` (cockpit) and
 * `/srv/gtcc1` (old console, `Dockerfile:99-100`), so `base === "/"`
 * matched both and gtcc1 rendered the cockpit
 * (`docs/operations/EDGE_CSP_ENFORCE_PROOF.md`). C-GTCC-LOGIN-SEP1
 * replaces it with `GT_ADMIN_SURFACE`, a build input pinned per `RUN`
 * line — one value per artifact, and the same value the `gt-surface`
 * meta marker carries (`packages/config/src/gtcc-surface.ts`).
 *
 * # D-GTCC-SHELL1 — the cockpit bundle always lands on the shell
 *
 * GTCC_1ZU1_PROGRAM.md Punkt 13: the Cockpit-root bundle IS the Control
 * Cockpit now, not a gated add-on to the classic Admin Overview — so a
 * cockpit-bundle login always opens the shell. `GtccShell` (not this
 * module) is where a non-owner's rights are enforced, via an in-shell
 * notice rather than a different landing page. `GTCC_SHELL_ENABLED` and
 * the owner check still gate the shell inside the `/admin/` bundle,
 * where this module keeps landing on "overview".
 */

import { isCockpitSurface, type ResolveAdminSurfaceOptions } from "@genius-tracker/config";

/** The pages this module can choose between. Both exist in `AdminPage`. */
export type AdminLandingPage = "gtcc" | "overview";

/**
 * True only for the Control-Cockpit artifact, i.e. the build that was
 * pinned to `GT_ADMIN_SURFACE=gtcc-cockpit`. A missing or unknown marker
 * (a non-vite host, a bare unit test, `vite dev`) is NOT the cockpit —
 * the narrower of the two surfaces is the safe default.
 */
export function isCockpitEntryBundle(env: ResolveAdminSurfaceOptions["env"]): boolean {
  return isCockpitSurface({ env: env ?? {} });
}

export interface InitialAdminPageInput {
  /** `import.meta.env` of the running bundle; carries `GT_ADMIN_SURFACE`. */
  readonly env: ResolveAdminSurfaceOptions["env"];
  /** Kept for the `/admin/`-bundle landing decision; the cockpit bundle no longer gates on this. */
  readonly isPlatformOwner: boolean;
  /** Kept for the `/admin/`-bundle landing decision; the cockpit bundle no longer gates on this. */
  readonly gtccShellEnabled: boolean;
}

/** The page the admin bundle opens on when it mounts after a login. */
export function resolveInitialAdminPage(input: InitialAdminPageInput): AdminLandingPage {
  if (isCockpitEntryBundle(input.env)) return "gtcc";
  return "overview";
}
